import { useState, useEffect, useRef } from "react";
import { Send, X, Sparkles, Loader2, Calendar, FileText, Mail } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { useLang } from "@/lib/i18n";

const ADELIS_PHOTO = "https://customer-assets.emergentagent.com/job_adelis-financial-hub/artifacts/jn4dase5_google8be87a01d2ad4b9a%20%281%29.html"; // placeholder; user can swap

// Use REACT_APP_BACKEND_URL; backend exposes /api/adelis/*
const BACKEND = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
const SESSION_KEY = "rn3_adelis_session";

const greeting = (lang) => lang === "es"
  ? "¡Hola! Soy Adelis AI™, la asistente virtual de Roman Numerals III. ¿En qué puedo ayudarte hoy?"
  : "Hello! I'm Adelis AI™, the virtual assistant for Roman Numerals III. What can I help you with today?";

// Lightweight markdown → safe HTML (bold, italic, links, line breaks).
// Escapes everything first, then re-injects allowed inline formatting.
const escapeHtml = (s) => s
  .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
  .replace(/"/g, "&quot;").replace(/'/g, "&#39;");

const renderMd = (raw) => {
  if (!raw) return "";
  // Process line by line so block-level constructs (headings, bullets, hr) render correctly
  const lines = raw.split("\n");
  const out = [];
  for (let line of lines) {
    let trimmed = line.trim();
    // Horizontal rule: --- or -- on its own line → drop it (no visual rule, no symbols)
    if (/^[-_*]{2,}$/.test(trimmed)) { out.push('<div style="height:6px"></div>'); continue; }
    // Strip leading heading markers (#, ##, ###, …) — with OR without trailing space
    const h = trimmed.match(/^#{1,6}\s*(.*)$/);
    if (h && trimmed.startsWith("#")) {
      const body = h[1].trim();
      if (!body) continue;
      const inner = inlineFmt(escapeHtml(body));
      out.push(`<div class="font-semibold mt-2 mb-1">${inner}</div>`);
      continue;
    }
    // Bullet: "- something", "* something", "• something" → "• something" (with OR without space)
    const b = line.match(/^(\s*)[-*•]\s*(.+)$/);
    if (b && /^[-*•]/.test(line.trimStart())) {
      const indent = b[1].length ? `padding-left:${Math.min(b[1].length, 4) * 8}px;` : "";
      const inner = inlineFmt(escapeHtml(b[2]));
      out.push(`<div style="${indent}">• ${inner}</div>`);
      continue;
    }
    // Plain line — keep newline by ending with <br/> (empty line becomes blank spacer)
    if (trimmed === "") { out.push('<div style="height:6px"></div>'); continue; }
    // Belt-and-suspenders: strip any stray leading "#" or "-" that wasn't caught above
    // (e.g. "#tag" with no space, "-quick note"). Preserve hyphens INSIDE words.
    let safe = line.replace(/^\s*#+\s*/g, "").replace(/^\s*-+\s+/g, "");
    out.push(`<div>${inlineFmt(escapeHtml(safe))}</div>`);
  }
  return out.join("");
};

// Inline formatting only — bold, links. Input must already be HTML-escaped.
// Italic (`*text*`) is intentionally NOT supported: the system prompt forbids
// it, and rendering it leaves orphan asterisks visible when Claude misuses it.
const inlineFmt = (s) => {
  // Markdown link [text](url) — http(s) or site-relative
  s = s.replace(/\[([^\]]+)\]\((https?:\/\/[^\s)]+|\/[^\s)]*)\)/g,
    '<a href="$2" target="_blank" rel="noopener noreferrer" class="underline text-[#8B1C22] hover:text-[#6B141A] break-all">$1</a>');
  // **bold**
  s = s.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
  // Auto-link bare URLs
  s = s.replace(/(^|[\s])(https?:\/\/[^\s<]+)/g,
    '$1<a href="$2" target="_blank" rel="noopener noreferrer" class="underline text-[#8B1C22] hover:text-[#6B141A] break-all">$2</a>');
  // Final pass: strip ANY remaining stray "*" characters (orphan asterisks
  // from unbalanced markdown that Claude may emit despite the system prompt).
  s = s.replace(/\*+/g, "");
  return s;
};

const AdelisAI = () => {
  const { lang } = useLang();
  const [open, setOpen] = useState(false);
  const [sessionId, setSessionId] = useState(() => {
    try { return localStorage.getItem(SESSION_KEY) || ""; } catch { return ""; }
  });
  const [messages, setMessages] = useState([{ role: "assistant", content: greeting(lang) }]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [showLeadForm, setShowLeadForm] = useState(false);
  const [showEscalate, setShowEscalate] = useState(false);
  const [lead, setLead] = useState({ full_name: "", email: "", phone: "", service_needed: "" });
  const [escVisitor, setEscVisitor] = useState({ visitor_name: "", visitor_email: "" });
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, streaming]);

  // Refresh greeting when language toggles (only if it's still the only message).
  // If the conversation has progressed, append a soft system notice so the user
  // sees that Adelis will continue in the new language on the next reply.
  const prevLangRef = useRef(lang);
  useEffect(() => {
    const prev = prevLangRef.current;
    prevLangRef.current = lang;
    if (prev === lang) return;
    setMessages((m) => {
      if (m.length <= 1 && m[0]?.role === "assistant") {
        return [{ role: "assistant", content: greeting(lang) }];
      }
      const note = lang === "es"
        ? "Cambié a español. Continuaré nuestra conversación en español. ¿En qué más puedo ayudarte?"
        : "Switched to English. I'll continue our conversation in English. How else can I help?";
      return [...m, { role: "assistant", content: note }];
    });
  }, [lang]);

  const send = async () => {
    const text = input.trim();
    if (!text || streaming) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: text }, { role: "assistant", content: "" }]);
    setStreaming(true);

    try {
      const res = await fetch(`${BACKEND}/api/adelis/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          session_id: sessionId || null,
          message: text,
          lang,
          // When the user is on a specific book (Statements/Register/Reports),
          // pass the id so the chatbot loads that book's active rules into
          // its context and can answer questions about them.
          book_id: (typeof window !== "undefined" && window.__RN3_ACTIVE_BOOK_ID__) || null,
        }),
      });
      if (!res.ok || !res.body) throw new Error("Network error");
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      let assistantText = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        // SSE frames separated by \n\n
        const frames = buf.split("\n\n");
        buf = frames.pop() || "";
        for (const frame of frames) {
          const lines = frame.split("\n");
          let eventName = "message";
          let data = "";
          for (const ln of lines) {
            if (ln.startsWith("event:")) eventName = ln.slice(6).trim();
            else if (ln.startsWith("data:")) data += ln.slice(5).trim();
          }
          if (eventName === "session") {
            if (data && data !== sessionId) {
              setSessionId(data);
              try { localStorage.setItem(SESSION_KEY, data); } catch { /* noop */ }
            }
          } else if (eventName === "done") {
            // end
          } else if (eventName === "error") {
            assistantText += `\n\n(Error: ${data})`;
          } else {
            // Chunk is JSON-encoded to preserve whitespace
            let chunk = "";
            try { chunk = JSON.parse(data); } catch { chunk = data; }
            assistantText += chunk;
            setMessages((m) => {
              const copy = [...m];
              copy[copy.length - 1] = { role: "assistant", content: assistantText };
              return copy;
            });
          }
        }
      }
    } catch (e) {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", content: lang === "es" ? "Lo siento — algo falló. Intenta de nuevo o llámanos al 904-776-8318." : "Sorry — something went wrong. Please try again or call 904-776-8318." };
        return copy;
      });
    } finally {
      setStreaming(false);
    }
  };

  const submitLead = async (e) => {
    e.preventDefault();
    if (!lead.full_name || !lead.email) {
      toast.error(lang === "es" ? "Nombre y correo son obligatorios" : "Name and email required");
      return;
    }
    try {
      await fetch(`${BACKEND}/api/adelis/lead`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...lead, session_id: sessionId, preferred_language: lang }),
      });
      toast.success(lang === "es" ? "¡Información guardada! Adelis te contactará pronto." : "Captured! Adelis will be in touch soon.");
      setShowLeadForm(false);
      setMessages((m) => [...m, { role: "assistant", content: lang === "es"
        ? "Perfecto — guardé tus datos. ¿Quieres terminar el registro en /intake (5 min) o agendar una llamada gratis en /appointments?"
        : "Perfect — I've saved your info. Want to finish onboarding at /intake (5 min) or book a free discovery call at /appointments?" }]);
    } catch { toast.error("Failed"); }
  };

  const submitEscalate = async (e) => {
    e.preventDefault();
    try {
      await fetch(`${BACKEND}/api/adelis/escalate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId, ...escVisitor, reason: "user_requested" }),
      });
      toast.success(lang === "es" ? "Enviado a Adelis — te contactará pronto" : "Sent to Adelis — she'll reply soon");
      setShowEscalate(false);
    } catch { toast.error("Failed"); }
  };

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        className="fixed bottom-5 right-5 z-[55] flex items-center gap-2 bg-[#8B1C22] text-white px-4 py-3 rounded-full shadow-xl hover:bg-[#6B141A] transition-all"
        data-testid="adelis-open-btn"
        aria-label="Open Adelis AI chat"
      >
        <Sparkles className="w-4 h-4 text-[#C4A252]" />
        <span className="text-sm font-medium hidden sm:inline">{lang === "es" ? "Habla con Adelis AI" : "Chat with Adelis AI"}</span>
      </button>
    );
  }

  return (
    <div className="fixed inset-0 sm:inset-auto sm:bottom-5 sm:right-5 z-[55] flex sm:block" data-testid="adelis-chat-panel">
      <div className="bg-white w-full sm:w-[380px] h-full sm:h-[600px] flex flex-col border border-[#E5E5E5] shadow-2xl">
        <div className="bg-[#8B1C22] text-white p-3 flex items-center justify-between">
          <div className="flex items-center gap-2 min-w-0">
            <div className="w-9 h-9 rounded-full bg-[#C4A252] flex items-center justify-center text-[#0A0A0A] font-bold flex-shrink-0">A</div>
            <div className="min-w-0">
              <div className="text-sm font-semibold truncate">Adelis AI™</div>
              <div className="text-[10px] uppercase tracking-widest text-[#C4A252]">{lang === "es" ? "Asistente Virtual" : "Virtual Assistant"}</div>
            </div>
          </div>
          <button onClick={() => setOpen(false)} className="text-white/80 hover:text-white" data-testid="adelis-close-btn" aria-label="Close chat">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div ref={scrollRef} className="flex-1 overflow-y-auto p-3 space-y-3 bg-[#FDFBF7]" data-testid="adelis-messages">
          {messages.map((m, i) => (
            <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
              <div
                className={`max-w-[85%] px-3 py-2 text-sm break-words overflow-wrap-anywhere ${m.role === "user" ? "bg-[#0A0A0A] text-white" : "bg-white border border-[#E5E5E5] text-[#0A0A0A]"}`}
                style={{ overflowWrap: "anywhere", wordBreak: "break-word" }}
              >
                {m.role === "assistant"
                  ? (m.content
                      ? <span dangerouslySetInnerHTML={{ __html: renderMd(m.content) }} />
                      : (streaming && i === messages.length - 1
                          ? <Loader2 className="w-4 h-4 animate-spin text-[#C4A252]" />
                          : null))
                  : <span className="whitespace-pre-wrap">{m.content}</span>}
              </div>
            </div>
          ))}
        </div>

        <div className="border-t border-[#E5E5E5] bg-white px-3 py-2 flex items-center gap-2 flex-wrap">
          <button onClick={() => setShowLeadForm(true)} className="text-[10px] uppercase tracking-widest text-[#737373] hover:text-[#8B1C22] inline-flex items-center gap-1" data-testid="adelis-share-info">
            <Mail className="w-3 h-3" /> {lang === "es" ? "Compartir información" : "Share my info"}
          </button>
          <Link to="/appointments" onClick={() => setOpen(false)} className="text-[10px] uppercase tracking-widest text-[#737373] hover:text-[#8B1C22] inline-flex items-center gap-1" data-testid="adelis-book">
            <Calendar className="w-3 h-3" /> {lang === "es" ? "Agendar" : "Book a call"}
          </Link>
          <Link to="/intake" onClick={() => setOpen(false)} className="text-[10px] uppercase tracking-widest text-[#737373] hover:text-[#8B1C22] inline-flex items-center gap-1" data-testid="adelis-intake">
            <FileText className="w-3 h-3" /> {lang === "es" ? "Nuevo cliente" : "New client form"}
          </Link>
          <button onClick={() => setShowEscalate(true)} className="text-[10px] uppercase tracking-widest text-[#737373] hover:text-[#8B1C22] ml-auto" data-testid="adelis-escalate">
            {lang === "es" ? "Hablar con Adelis" : "Talk to Adelis"}
          </button>
        </div>

        <form onSubmit={(e) => { e.preventDefault(); send(); }} className="border-t border-[#E5E5E5] p-2 flex items-center gap-2">
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder={lang === "es" ? "Escribe tu pregunta…" : "Ask Adelis AI anything…"}
            className="flex-1 px-3 py-2 text-sm border border-[#E5E5E5] focus:outline-none focus:border-[#8B1C22]"
            disabled={streaming}
            data-testid="adelis-input"
            aria-label="Message"
          />
          <button type="submit" disabled={streaming || !input.trim()} className="p-2 bg-[#8B1C22] text-white disabled:opacity-40" data-testid="adelis-send" aria-label="Send">
            {streaming ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          </button>
        </form>

        {/* Lead form */}
        {showLeadForm && (
          <div className="absolute inset-0 sm:inset-auto sm:absolute sm:bottom-0 sm:left-0 sm:right-0 sm:h-auto bg-white border-t border-[#C4A252] p-4 shadow-2xl" data-testid="adelis-lead-form">
            <div className="text-xs uppercase tracking-widest text-[#8B1C22] mb-2 font-semibold">{lang === "es" ? "Comparte tu información" : "Share your info"}</div>
            <form onSubmit={submitLead} className="space-y-2">
              <input required value={lead.full_name} onChange={(e) => setLead({ ...lead, full_name: e.target.value })} placeholder={lang === "es" ? "Nombre completo *" : "Full name *"} className="w-full px-2 py-1.5 text-sm border border-[#E5E5E5]" data-testid="adelis-lead-name" />
              <input required type="email" value={lead.email} onChange={(e) => setLead({ ...lead, email: e.target.value })} placeholder="Email *" className="w-full px-2 py-1.5 text-sm border border-[#E5E5E5]" data-testid="adelis-lead-email" />
              <input value={lead.phone} onChange={(e) => setLead({ ...lead, phone: e.target.value })} placeholder={lang === "es" ? "Teléfono" : "Phone"} className="w-full px-2 py-1.5 text-sm border border-[#E5E5E5]" data-testid="adelis-lead-phone" />
              <input value={lead.service_needed} onChange={(e) => setLead({ ...lead, service_needed: e.target.value })} placeholder={lang === "es" ? "Servicio (ej. impuestos)" : "Service (e.g. bookkeeping)"} className="w-full px-2 py-1.5 text-sm border border-[#E5E5E5]" data-testid="adelis-lead-service" />
              <div className="flex gap-2 justify-end pt-1">
                <button type="button" onClick={() => setShowLeadForm(false)} className="text-xs text-[#737373] px-2">{lang === "es" ? "Cancelar" : "Cancel"}</button>
                <button type="submit" className="bg-[#8B1C22] text-white text-xs px-3 py-1.5" data-testid="adelis-lead-submit">{lang === "es" ? "Enviar" : "Submit"}</button>
              </div>
            </form>
          </div>
        )}

        {/* Escalation form */}
        {showEscalate && (
          <div className="absolute inset-0 sm:inset-auto sm:absolute sm:bottom-0 sm:left-0 sm:right-0 sm:h-auto bg-white border-t border-[#8B1C22] p-4 shadow-2xl" data-testid="adelis-escalate-form">
            <div className="text-xs uppercase tracking-widest text-[#8B1C22] mb-2 font-semibold">{lang === "es" ? "Enviar conversación a Adelis" : "Send conversation to Adelis"}</div>
            <p className="text-[11px] text-[#737373] mb-2">{lang === "es" ? "Adelis recibirá un correo con el historial y te responderá personalmente." : "Adelis will receive the transcript by email and will personally reply."}</p>
            <form onSubmit={submitEscalate} className="space-y-2">
              <input value={escVisitor.visitor_name} onChange={(e) => setEscVisitor({ ...escVisitor, visitor_name: e.target.value })} placeholder={lang === "es" ? "Tu nombre" : "Your name"} className="w-full px-2 py-1.5 text-sm border border-[#E5E5E5]" data-testid="adelis-esc-name" />
              <input type="email" value={escVisitor.visitor_email} onChange={(e) => setEscVisitor({ ...escVisitor, visitor_email: e.target.value })} placeholder={lang === "es" ? "Tu correo (para que te respondan)" : "Your email (so she can reply)"} className="w-full px-2 py-1.5 text-sm border border-[#E5E5E5]" data-testid="adelis-esc-email" />
              <div className="flex gap-2 justify-end pt-1">
                <button type="button" onClick={() => setShowEscalate(false)} className="text-xs text-[#737373] px-2">{lang === "es" ? "Cancelar" : "Cancel"}</button>
                <button type="submit" className="bg-[#8B1C22] text-white text-xs px-3 py-1.5" data-testid="adelis-esc-submit">{lang === "es" ? "Enviar" : "Send"}</button>
              </div>
            </form>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdelisAI;
